import { fipsToStateAndCounty, getStateFIPS } from "./FIPSUtilities"
import { LayerKind, fipsToLayerKind } from "./Globe"

/**
 * States relating to browser History (window.history)
 */
export enum HistoryStateType {
    initial,
    globe,
    map,
}

export interface InitialHistoryState {
    type: HistoryStateType.initial
    prev: null
}
export function initialHistoryState(): InitialHistoryState {
    return {
        type: HistoryStateType.initial,
        prev: null,
    }
}

/**
 * This type of state applies when users are interacting with the 3D globe.
 */
export interface GlobeHistoryState {
    type: HistoryStateType.globe
    fips: FIPSCode
    prev: GlobeHistoryState | InitialHistoryState | null
}
interface GlobeHistoryStateOptions {
    fips: FIPSCode
    prev?: GlobeHistoryState | InitialHistoryState | null
}
function fillGlobeHistoryStateOptions(
    options: GlobeHistoryStateOptions
): Required<GlobeHistoryStateOptions> {
    if (options.prev === undefined) {
        options.prev = null
    }
    return options as Required<GlobeHistoryStateOptions>
}
export function globeHistoryState(
    options: GlobeHistoryStateOptions
): GlobeHistoryState {
    const filledOptions = fillGlobeHistoryStateOptions(options)
    return {
        ...filledOptions,
        type: HistoryStateType.globe,
    }
}

/**
 * This type of state applies when users are interacting with the 2D map.
 */
export interface MapHistoryState {
    type: HistoryStateType.map
    lat: number
    lon: number
    usCounty: string
    usState: string
    districtId: string
    electionFilter: string
    electionType: number
    prev: PrevHistoryState
}

interface MapHistoryStateOptions {
    lat: number
    lon: number
    usCounty: string
    usState: string
    districtId: string
    electionFilter: string
    electionType: number
    prev?: PrevHistoryState
}
function fillMapHistoryStateOptions(
    options: MapHistoryStateOptions
): Required<MapHistoryStateOptions> {
    if (options.prev === undefined) {
        options.prev = null
    }
    return options as Required<MapHistoryStateOptions>
}
export function mapHistoryState(
    options: MapHistoryStateOptions
): MapHistoryState {
    const filledOptions = fillMapHistoryStateOptions(options)
    return {
        ...filledOptions,
        type: HistoryStateType.map,
    }
}

type PrevHistoryState = GlobeHistoryState | InitialHistoryState | null

export type HistoryState =
    | InitialHistoryState
    | GlobeHistoryState
    | MapHistoryState

/**
 * The popstate inaction flag is necessary to
 */
let historyPopInactionCounter = 0
export function doNothingOnNextNPopstates(n: number): void {
    historyPopInactionCounter += n
}
export function checkPopstateInaction(): boolean {
    return historyPopInactionCounter > 0
}
export function notifyPopstateInaction(): void {
    historyPopInactionCounter -= 1
}

let historyOnNextPopstate: HistoryState | null = null
export function setHistoryOnNextPopstate(historyState: HistoryState): void {
    historyOnNextPopstate = historyState
}
export function notifyHistorySetOnPopstate(): void {
    historyOnNextPopstate = null
}

let prevHistoryState: null | HistoryState = urlToHistoryState(
    window.location.href
)

export function getPrevHistoryState(): null | HistoryState {
    return prevHistoryState
}

export function setPrevHistoryState(historyState: HistoryState): void {
    prevHistoryState = typeCoercedHistoryState(historyState)
}

export function historyStateToURL(historyState: HistoryState): string {
    const state = typeCoercedHistoryState(historyState)
    if (state.type === HistoryStateType.initial) {
        return ""
    } else if (state.type === HistoryStateType.globe) {
        return `?fips=${state.fips}`
    } else if (state.type === HistoryStateType.map) {
        const lat = state.lat
        const lon = state.lon
        const usCounty = state.usCounty.replaceAll(" ", "_")
        const usState = state.usState
        const districtId = state.districtId
        const electionFilter = state.electionFilter
        const electionType = state.electionType
        return `?lng=${lon}&lat=${lat}&county=${usCounty}&state=${usState}&district_id=${districtId}&election_filter=${electionFilter}&election_type=${electionType}`
    }
    return ""
}

export function urlToHistoryState(url: string | URL): HistoryState {
    // Type validation
    if (typeof url === "string") {
        url = new URL(url)
    } else if (!(url instanceof URL)) {
        const err = new Error("url param was neither string nor typeof URL")
        console.error({ err, url })
        return initialHistoryState()
    }
    const searchParams = url.searchParams
    // Case: Initial
    if (searchParams.size === 0) {
        return initialHistoryState()
    }
    // Case: Globe state (county level, zip code level)
    if (searchParams.has("fips")) {
        return globeHistoryState({
            fips: searchParams.get("fips") as string,
        })
    }
    // Case: Map state
    const lat = Number(searchParams.get("lat"))
    const lng = Number(searchParams.get("lng"))
    const usCounty = searchParams.get("county")
    if (usCounty === null) {
        const err = new Error("invalid URL parameter")
        console.error({ err, usCounty })
        return initialHistoryState()
    }
    const usState = searchParams.get("state")
    if (usState === null) {
        const err = new Error("invalid URL parameter")
        console.error({ err, usState })
        return initialHistoryState()
    }
    let districtId = searchParams.get("district_id")
    if (districtId === null) {
        districtId = "null"
    }
    let electionFilter = searchParams.get("election_filter")
    if (electionFilter === null) {
        electionFilter = "null"
    }
    const electionType = Number(searchParams.get("election_type"))
    const state = mapHistoryState({
        lat,
        lon: lng,
        usCounty,
        usState,
        districtId,
        electionFilter,
        electionType,
    })
    return typeCoercedHistoryState(state)
}

/**
 * Coerces the historyState into a valid history state.
 *
 * Replaces invalid values with sane defaults.
 * @param historyState
 * @returns A new historyState object with coerced values.
 */
export function typeCoercedHistoryState(historyState): HistoryState {
    const clone = JSON.parse(JSON.stringify(historyState)) as HistoryState
    if (clone.type === HistoryStateType.map) {
        if (isNaN(clone.electionType)) {
            clone.electionType = 0
        }
    }
    return clone
}

/**
 * Make the necessary changes to the history stack to transition to historyState
 * @param historyState
 */
export function updateHistory(historyState: HistoryState): void {
    console.log({ historyState, prevHistoryState }, "update history")
    const next = typeCoercedHistoryState(historyState)
    const diff = historyDiff(historyState)
    const url = historyStateToURL(historyState)
    if (diff === 0) {
        // Case: moving to a history state at the same stack level
        // Hand off the .prev of our old history state to our new history state
        if (prevHistoryState === null) {
            next.prev = null
        } else {
            next.prev = prevHistoryState.prev
        }
        history.replaceState(next, "", url)
    } else if (diff < 0) {
        // Case: moving to a history state up the stack (toward the past)
        // sanity check
        if (history.state.prev === undefined) {
            const err = new Error("history state prev was undefined")
            throw err
        }
        // traverse back up the stack
        let nBack = 0
        let prev = history.state
        for (let i = 0; i < diff * -1; i++) {
            prev = prev.prev
            if (prev !== null) {
                // Pop diff amount of states
                nBack += 1
            }
        }
        // Preserve prev
        if (prev === null) {
            next.prev = null
        } else {
            next.prev = prev.prev
        }
        if (nBack > 0) {
            doNothingOnNextNPopstates(1)
            history.go(nBack * -1)
        }
        // Change our current state to our destination state
        // history.go is asynchronous, so we can't just history.replaceState right away
        addEventListener(
            "popstate",
            (event) => {
                history.replaceState(next, "", url)
            },
            { once: true }
        )
    } else if (diff > 0) {
        // Case: moving to a history state down the stack (toward the future)
        next.prev = prevHistoryState as
            | InitialHistoryState
            | GlobeHistoryState
            | null
        history.pushState(next, "", url)
    }
    prevHistoryState = next
}

/**
 * Calculate the integer difference in the history stack from one state to another
 * @param historyState
 */
function historyDiff(historyState: HistoryState): number {
    if (prevHistoryState === null) {
        return 1
    }
    return historyStackPos(historyState) - historyStackPos(prevHistoryState)
}

/**
 * Get the stack position of this history state
 * @param historyState
 */
function historyStackPos(historyState: HistoryState): number {
    if (historyState.type === HistoryStateType.initial) {
        // Initial level
        return 0
    }
    if (historyState.type === HistoryStateType.globe) {
        const fips = historyState.fips
        const layer = fipsToLayerKind(fips)
        if (layer === LayerKind.COUNTIES) {
            // County overview level
            return 1
        } else {
            // Individual county level (zip level)
            return 2
        }
    }
    // Map level
    return 3
}

/**
 * Return a historyState one level up
 * @param historyState
 * @returns
 */
function upOne(historyState: HistoryState): HistoryState {
    const stackPos = historyStackPos(historyState)
    if (stackPos === 0) {
        console.warn("already at the top of the stack")
        return historyState
    }
    if (historyState.type === HistoryStateType.map) {
        const next: GlobeHistoryState = {
            type: HistoryStateType.globe,
            fips: historyState.fips,
        }
        return next
    }
    if (historyState.type === HistoryStateType.globe) {
        const layer = fipsToLayerKind(historyState.fips)
        if (layer === LayerKind.INDIVIDUAL_COUNTY) {
            const next: GlobeHistoryState = {
                type: HistoryStateType.globe,
                fips: getStateFIPS(historyState.fips),
            }
            return next
        } else if (layer === LayerKind.COUNTIES) {
            const next: InitialHistoryState = {
                type: HistoryStateType.initial,
            }
            return next
        }
    }
    return typeCoercedHistoryState(historyState)
}

/**
 * Generate sequence to get from a higher history state to a lower one.
 *
 * @param start
 * @param end
 */
function historyRange(start: HistoryState, end: HistoryState): HistoryState[] {
    let current = end
    const states: HistoryState[] = []
    states.unshift(current)
    while (historyStackPos(current) > historyStackPos(start)) {
        current = upOne(current)
        states.unshift(current)
    }
    return states
}
